import { Checkbox, FormHelperText, IconButton, MenuItem, Typography } from '@material-ui/core';
import { LocationOn, Visibility, VisibilityOff, WhatsApp } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import { isValidNumber } from 'libphonenumber-js';
import FacebookLogin from 'react-facebook-login';
import AppleLogin from 'react-apple-login';
import { useFormikContext } from 'formik';
import { useHistory } from 'react-router';
import toast from 'react-hot-toast';

import { decodedToken, userAuthService } from '@project/services';
import { FacebookResponse, GoogleResponse } from '@project/types';
import { ProvinceDescription, provinceValues } from '@project/enums';
import { useRoleRedirect } from '@project/hooks';
import { useAppDispatch } from '@project/reducers/store';
import { flattenErrors } from '@project/helpers/error-helpers';
import { setUserRole } from '@project/reducers/userRole-reducer/userRole.actions';
import { Button, FormTextField } from '@project/components';

import appConfig from '../../../config';
import { SignUpV2FormValues } from './sign-up-process';

type SignUpV2FormProps = {
  submitForm: (formData: any) => any;
  onSuccess: () => unknown;
  onFailure: () => unknown;
  isLoading: boolean;
};

type FieldLabelProps = {
  children: React.ReactNode;
  required?: boolean;
};

const FieldLabel: React.FC<FieldLabelProps> = ({ children, required = false }) => (
  <div className="flex flex-col w-4/5 self-center">
    <small className="text-left">
      {children}
      {required ? (
        <span className="text-danger" aria-hidden="true">
          {' *'}
        </span>
      ) : (
        <span className="text-gray-500">{' (optional)'}</span>
      )}
    </small>
  </div>
);

export const SignUpV2Form: React.FC<SignUpV2FormProps> = ({
  submitForm,
  onFailure,
  isLoading = false,
}) => {
  const history = useHistory();
  const redirectByRole = useRoleRedirect();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const dispatch = useAppDispatch();
  const { values, handleChange, touched, errors, handleBlur, setFieldError } =
    useFormikContext<SignUpV2FormValues>();
  const _onLoginClick = () => {
    history.push('/login');
  };

  const _onTermsClick = () => {
    window.open('https://finessa.co.za/terms-and-conditions', '_blank');
  };

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  function _toggleShowPassword() {
    setShowPassword(!showPassword);
  }

  const redirectBasedOnRole = (role: string) => {
    redirectByRole(role);
  };

  function _firstFormSetHasErrors(): boolean {
    const internationalNumber = `+${values.account.code}${values.account.cellphone}`;
    if (!isValidNumber(internationalNumber)) {
      setFieldError('account.cellphone', 'Invalid Phone number');
      return true;
    }
    return (
      (errors?.account?.password ||
        errors?.account?.confirmPassword ||
        errors?.account?.email ||
        errors?.account?.confirmEmail ||
        errors?.account?.firstName ||
        errors?.account?.lastName ||
        errors?.account?.termsChecked ||
        errors?.account?.province) !== undefined
    );
  }

  const onNext = (params) => {
    if (_firstFormSetHasErrors()) {
      toast.error(
        <span>
          {flattenErrors(errors?.account).map((line) => (
            <span key={line}>
              {line}
              <br />
            </span>
          ))}
        </span>,
      );
      return;
    }
    submitForm(params);
  };

  function _toggleShowConfirmPassword() {
    setShowConfirmPassword(!showConfirmPassword);
  }

  const responseFacebook = async (response: FacebookResponse) => {
    if (!response.accessToken) {
      return;
    }

    const data = await userAuthService.facebookExternalSignIn({
      provider: 'facebook',
      accessToken: response.accessToken,
    });

    if (data === false) {
      onFailure();

      return;
    }

    const role = decodedToken.getRole();
    await dispatch(setUserRole(role));
    redirectBasedOnRole(role);
  };

  const responseGoogle = async (response: GoogleResponse) => {
    if (!response.credential) {
      return;
    }

    const data = await userAuthService.googleExternalSignIn({
      provider: 'google',
      credential: response.credential,
    });

    if (data === false) {
      onFailure();

      return;
    }

    const role = decodedToken.getRole();
    await dispatch(setUserRole(role));
    redirectBasedOnRole(role);
  };

  const responseApple = async (response) => {
    if (!response.authorization) {
      return;
    }
    const idToken = response.authorization.id_token;

    const data = await userAuthService.appleExternalSignIn({ provider: 'apple', idToken });

    if (data === false) {
      onFailure();

      return;
    }

    const role = decodedToken.getRole();
    await dispatch(setUserRole(role));
    redirectBasedOnRole(role);
  };

  const passwordButtonIcon = showPassword ? <Visibility /> : <VisibilityOff />;
  const confirmPasswordButtonIcon = showConfirmPassword ? <Visibility /> : <VisibilityOff />;

  useEffect(() => {
    if (sessionStorage.getItem('preSignUpEmail')) {
      sessionStorage.removeItem('preSignUpEmail');
    }

    const initializeGoogleSignIn = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: appConfig.googleClientId,
          callback: responseGoogle,
          auto_select: false,
          cancel_on_tap_outside: true,
          use_fedcm_for_prompt: false,
        });
      }
    };

    if (!window.google) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleSignIn;
      document.head.appendChild(script);
    } else {
      initializeGoogleSignIn();
    }
  }, []);

  const handleGoogleSignIn = () => {
    if (window.google) {
      setIsGoogleLoading(true);

      window.google.accounts.id.prompt((notification) => {
        setIsGoogleLoading(false);

        if (notification.isNotDisplayed()) {
          const reason = notification.getNotDisplayedReason();

          if (
            reason === 'browser_not_supported' ||
            reason === 'suppressed_by_user' ||
            reason === 'unknown_reason'
          ) {
            toast.error(
              'Google sign-in was skipped. Please try again using your email and password..',
            );
          }
        } else if (notification.isSkippedMoment()) {
          toast.error(
            'Google sign-in failed. Please enable third-party sign-in or use your email and password.',
          );
        }
      });
    }
  };

  return (
    <form className="flex flex-auto flex-col gap-3 py-2 items-center">
      <div className="w-full max-w-md mx-auto space-y-6 text-center">
        <h2 className="text-2xl font-semibold">Sign up</h2>
      </div>
      <div className="flex space-x-2 w-4/5 h-10">
        <div className="flex items-center text-sm justify-center w-full px-1 bg-blue-600 hover:bg-blue-400 text-white rounded-lg shadow-md transition duration-150 ease-in-out max-sm:w-12 max-sm:h-12 max-sm:rounded-full max-sm:p-0">
          <FacebookLogin
            appId={appConfig.facebookAppId}
            autoLoad={false}
            fields="name,email"
            icon="fa-facebook"
            callback={responseFacebook}
            textButton=" Sign up with Facebook"
            cssClass="my-facebook-button-class hidden sm:block"
          />
          <div className="sm:hidden flex items-center justify-center w-full h-full">
            <FacebookLogin
              appId={appConfig.facebookAppId}
              autoLoad={false}
              fields="name,email"
              icon="fa-facebook"
              callback={responseFacebook}
              textButton=""
              cssClass="facebook-icon-only"
            />
          </div>
        </div>
        <div className="flex items-center justify-center w-full h-10 px-1 bg-white hover:bg-gray-200 text-gray-800 rounded-lg border border-gray-300 shadow-md transition duration-150 ease-in-out">
          <button
            onClick={handleGoogleSignIn}
            type="button"
            className="flex items-center justify-center w-full h-full max-sm:p-0"
          >
            <div className="bg-white rounded-full p-1 sm:mr-2 max-sm:p-0">
              {isGoogleLoading ? (
                <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
              ) : (
                <img src="assets/icons/google.png" alt="Google" className="w-5 h-5" />
              )}
            </div>
            <span className="text-sm font-medium sm:block hidden">
              {isGoogleLoading ? 'Loading...' : 'Sign up with Google'}
            </span>
          </button>
        </div>
        <AppleLogin
          clientId={appConfig.appleClientId}
          redirectURI={window.location.origin}
          responseType="code id_token"
          responseMode="fragment"
          usePopup
          callback={responseApple}
          scope="name email"
          render={(renderProps) => (
            <button
              onClick={renderProps.onClick}
              type="button"
              className="flex items-center text-sm justify-center w-full px-1 bg-black hover:bg-gray-800 text-white rounded-lg shadow-md transition duration-150 ease-in-out max-sm:w-12 max-sm:h-12 max-sm:rounded-full max-sm:p-0"
            >
              <img
                src="assets/icons/apple-icon.svg"
                alt="Apple logo"
                className="w-5 h-5 sm:mr-2 max-sm:mr-0"
              />
              <span className="sm:block hidden text-center">Sign up with Apple</span>
            </button>
          )}
        />
      </div>
      <p className="text-text-grey self-center w-4/5">
        Or please enter your details so we can check your fines
      </p>
      <FieldLabel required>First Name</FieldLabel>
      <FormTextField
        variableName="account.firstName"
        placeholder="First Name"
        values={values}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched}
        errors={errors}
        icon={<img src="assets/icons/user-icon.png" alt="userName" />}
        className="w-4/5 self-center"
      />
      <FieldLabel required>Last Name</FieldLabel>
      <FormTextField
        variableName="account.lastName"
        placeholder="Last Name"
        values={values}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched}
        errors={errors}
        icon={<img src="assets/icons/user-icon.png" alt="lastName" />}
        className="w-4/5 self-center"
      />
      <FieldLabel required>Email</FieldLabel>
      <FormTextField
        variableName="account.email"
        placeholder="Email"
        values={values}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched}
        errors={errors}
        icon={<img src="assets/icons/mail-icon.png" alt="mail" />}
        className="w-4/5 self-center"
      />
      <FieldLabel required>Confirm Email</FieldLabel>
      <FormTextField
        variableName="account.confirmEmail"
        placeholder="Confirm Email"
        values={values}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched}
        errors={errors}
        icon={<img src="assets/icons/mail-icon.png" alt="mail" />}
        className="w-4/5 self-center"
      />
      <FieldLabel required>Cellphone</FieldLabel>
      <div className="w-4/5 self-center flex justify-between">
        <FormTextField
          variableName="account.code"
          placeholder="Code"
          values={values}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched}
          errors={errors}
          icon={<img src="assets/icons/plus-icon.png" alt="code" />}
          className="w-3/12"
        />
        <FormTextField
          variableName="account.cellphone"
          placeholder="Cellphone"
          values={values}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched}
          errors={errors}
          icon={<img src="assets/icons/phone-icon.png" alt="phone" />}
          className="w-8/12"
        />
      </div>
      <FieldLabel>WhatsApp Phone Number</FieldLabel>
      <div className="w-4/5 self-center flex justify-between">
        <FormTextField
          variableName="account.whatsAppCode"
          placeholder="Code"
          values={values}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched}
          errors={errors}
          icon={<img src="assets/icons/plus-icon.png" alt="code" />}
          className="w-3/12"
        />
        <FormTextField
          variableName="account.whatsAppCellphone"
          placeholder="WhatsApp Cellphone"
          values={values}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched}
          errors={errors}
          icon={<img src="assets/icons/phone-icon.png" alt="phone" />}
          className="w-8/12"
        />
      </div>
      <FieldLabel required>Province</FieldLabel>
      <FormTextField
        variableName="account.province"
        placeholder="Province"
        values={values}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched}
        errors={errors}
        icon={<LocationOn className="text-gray-500" />}
        className="w-4/5 self-center"
        select
      >
        <MenuItem value="" className="font-thin">
          Select Province
        </MenuItem>
        {provinceValues.map((province) => (
          <MenuItem key={province} value={province}>
            {ProvinceDescription[province]}
          </MenuItem>
        ))}
      </FormTextField>
      <FieldLabel required>Password</FieldLabel>
      <div className="w-4/5">
        <Typography className="text-gray-500 font-light mb-1 text-xs">
          Password must be at least 6 characters long
        </Typography>

        <FormTextField
          variableName="account.password"
          placeholder="Create a Password"
          type={showPassword ? 'text' : 'password'}
          values={values}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched}
          errors={errors}
          icon={<img src="assets/icons/lock-icon.png" alt="password" />}
          endIcon={
            <IconButton size="small" onClick={_toggleShowPassword}>
              {passwordButtonIcon}
            </IconButton>
          }
          className="w-full self-center"
        />
      </div>

      <FieldLabel required>Confirm Password</FieldLabel>
      <FormTextField
        variableName="account.confirmPassword"
        placeholder="Confirm Password"
        type={showConfirmPassword ? 'text' : 'password'}
        values={values}
        onChange={handleChange}
        onBlur={handleBlur}
        touched={touched}
        errors={errors}
        icon={<img src="assets/icons/lock-icon.png" alt="passwordConfirm" />}
        endIcon={
          <IconButton size="small" onClick={_toggleShowConfirmPassword}>
            {confirmPasswordButtonIcon}
          </IconButton>
        }
        className="w-4/5 self-center"
      />
      <div className="flex place-items-center w-4/5 self-center">
        <Checkbox
          checked={values.account.termsChecked}
          name="termsChecked"
          id="termsChecked"
          color="secondary"
          onChange={handleChange('account.termsChecked')}
          onBlur={handleBlur('account.termsChecked')}
        />

        <div className="flex flex-row">
          <Typography className="self-center">{'Agree to the '}</Typography>
          <Button type="text" className="font-bold" onClick={_onTermsClick}>
            Terms & Conditions
          </Button>
        </div>
      </div>
      {errors?.account?.termsChecked && (
        <FormHelperText error>{errors?.account?.termsChecked}</FormHelperText>
      )}
      <div className="flex place-items-center w-4/5 self-center">
        <Checkbox
          checked={values.account.whatsAppOptedIn}
          name="whatsAppOptedIn"
          id="whatsAppOptedIn"
          color="secondary"
          onChange={handleChange('account.whatsAppOptedIn')}
          onBlur={handleBlur('account.whatsAppOptedIn')}
        />
        <div className="flex flex-row items-center">
          <Typography className="self-center">
            I want to receive FinesSA communications via WhatsApp{' '}
            <WhatsApp style={{ color: '#25D366' }} />
          </Typography>
        </div>
      </div>
      <Button isLoading={isLoading} onClick={onNext} className="auth-form-submit-button">
        Next
      </Button>
      <div className="self-center flex flex-row">
        <Typography className="self-center">{'Already have an account? '}</Typography>
        <Button type="text" className="font-bold -ml-1" onClick={_onLoginClick}>
          Log In
        </Button>
      </div>
    </form>
  );
};
